import React, { useEffect, useState, useCallback } from "react";
import { Copy, Plus, Trash2, Mail, Check, Ticket } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import { listMyInvitations, createInvitation, deleteInvitation } from "@/lib/db";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

export default function Convites() {
  const { clinicId } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", days_valid: 14, max_uses: 1 });
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setList(await listMyInvitations(clinicId)); } finally { setLoading(false); }
  }, [clinicId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await createInvitation({
        email: form.email || null,
        days_valid: Number(form.days_valid) || 14,
        max_uses: Number(form.max_uses) || 1,
        clinic_id: clinicId
      });
      setForm({ email: "", days_valid: 14, max_uses: 1 });
      load();
    } finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este convite?")) return;
    await deleteInvitation(id);
    load();
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="ds-fade-up">
      <PageHeader
        eyebrow="Aquisição"
        title="Convites para pacientes"
        description="Gere códigos únicos para vincular pacientes existentes em segundos. Códigos por convite são aprovados automaticamente."
      />

      {/* Generator */}
      <form onSubmit={submit} className="ds-card p-5 mb-6">
        <div className="ds-overline mb-3">Gerar novo convite</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="E-mail (opcional)">
            <input data-testid="inv-email" type="email" className="ds-input" placeholder="paciente@email.com"
                   value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
          </Field>
          <Field label="Validade (dias)">
            <input data-testid="inv-days" type="number" min="1" className="ds-input"
                   value={form.days_valid} onChange={e => setForm({...form, days_valid: e.target.value})} />
          </Field>
          <Field label="Usos permitidos">
            <input data-testid="inv-uses" type="number" min="1" className="ds-input"
                   value={form.max_uses} onChange={e => setForm({...form, max_uses: e.target.value})} />
          </Field>
        </div>
        <div className="mt-4 flex justify-end">
          <button data-testid="inv-create" disabled={creating} className="ds-btn-primary">
            <Plus className="w-4 h-4" />{creating ? "Gerando..." : "Gerar convite"}
          </button>
        </div>
      </form>

      {/* List */}
      {loading ? (
        <div className="ds-card p-10 text-center text-sm text-[hsl(var(--text-tertiary))]">Carregando...</div>
      ) : list.length === 0 ? (
        <EmptyState icon={Ticket} title="Nenhum convite gerado"
                    description="Gere um código acima e compartilhe com o paciente. Ao usar o código no cadastro, ele já fica vinculado a você." />
      ) : (
        <>
          {/* Mobile View: Cards (Visible on mobile, hidden on desktop) */}
          <div className="grid grid-cols-1 gap-4 md:hidden" data-testid="invitations-cards">
            {list.map((i) => (
              <div key={i.id} className="ds-card p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-2">
                    <code className="mono text-sm font-semibold tracking-wider px-2 py-1 rounded bg-[hsl(var(--brand-light))] text-[hsl(var(--brand))]">{i.code}</code>
                    <button onClick={() => copy(i.code)} className="text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--brand))]">
                      {copied === i.code ? <Check className="w-4 h-4 text-[hsl(var(--success-text))]" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <button onClick={() => remove(i.id)} className="text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--danger-text))] p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between border-b border-[hsl(var(--border-divider))] pb-2">
                    <span className="text-[hsl(var(--text-tertiary))] font-medium">E-mail</span>
                    <span className="text-[hsl(var(--text-primary))]">{i.email || "Aberto"}</span>
                  </div>
                  <div className="flex justify-between border-b border-[hsl(var(--border-divider))] pb-2">
                    <span className="text-[hsl(var(--text-tertiary))] font-medium">Usos</span>
                    <span className="text-[hsl(var(--text-primary))] font-semibold">{i.uses_count}/{i.max_uses}</span>
                  </div>
                  <div className="flex justify-between border-b border-[hsl(var(--border-divider))] pb-2">
                    <span className="text-[hsl(var(--text-tertiary))] font-medium">Expira em</span>
                    <span className="text-[hsl(var(--text-primary))]">{i.expires_at ? formatDate(i.expires_at) : "Nunca"}</span>
                  </div>
                  <div className="flex justify-between pt-1">
                    <span className="text-[hsl(var(--text-tertiary))] font-medium">Criado em</span>
                    <span className="text-[hsl(var(--text-tertiary))]">{formatDate(i.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop View: Table (Hidden on mobile, visible on desktop) */}
          <div className="hidden md:block ds-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-[hsl(var(--bg-base))]/60">
                  <Th>Código</Th>
                  <Th>E-mail</Th>
                  <Th>Usos</Th>
                  <Th>Expira em</Th>
                  <Th>Criado</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody data-testid="invitations-table">
                {list.map(i => (
                  <tr key={i.id} className="border-t border-[hsl(var(--border-divider))]">
                    <td className="px-4 py-3">
                      <div className="inline-flex items-center gap-2">
                        <code className="mono text-sm font-semibold tracking-wider px-2 py-1 rounded bg-[hsl(var(--brand-light))] text-[hsl(var(--brand))]">{i.code}</code>
                        <button onClick={() => copy(i.code)} data-testid={`inv-copy-${i.id}`} className="text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--brand))]" aria-label="Copiar">
                          {copied === i.code ? <Check className="w-4 h-4 text-[hsl(var(--success-text))]" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[hsl(var(--text-secondary))]">
                      {i.email ? <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{i.email}</span> : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm">{i.uses_count}/{i.max_uses}</td>
                    <td className="px-4 py-3 text-sm text-[hsl(var(--text-secondary))]">{i.expires_at ? formatDate(i.expires_at) : "—"}</td>
                    <td className="px-4 py-3 text-sm text-[hsl(var(--text-tertiary))]">{formatDate(i.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => remove(i.id)} className="text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--danger-text))]" aria-label="Excluir">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Th({ children }) { return <th className="text-left px-4 py-3 ds-overline whitespace-nowrap">{children}</th>; }
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
